<script setup lang="ts">
// Who pulls a pull destination (SRT listener, RTMP pull) in the window:
// each session with its address, when and how long, what it was sent and,
// over SRT, how much had to be resent; those connected now first and
// marked live. From the node's client log (pullclients.go), refreshed
// every 5 s while the window follows now.
import { computed, onUnmounted, ref, watch } from 'vue'
import { api, type PullClient } from '../../api'
import { useNode } from '../../store'
import { bytes, clock, dur } from '../../util'

const props = defineProps<{ id: string }>()
const node = useNode()
const list = ref<PullClient[] | null>(null)
const failed = ref(false)
const all = ref(false)
async function load() {
    try {
        const [from, to] = node.span
        list.value = await api.pullClients(props.id, Math.floor(from / 1000), Math.ceil(to / 1000))
        failed.value = false
    } catch {
        failed.value = true // an older node without the client log
    }
}
watch(
    () => [props.id, node.live ? 'live' : node.span.join()],
    () => void load(),
    { immediate: true },
)
const timer = window.setInterval(() => node.live && void load(), 5000)
onUnmounted(() => clearInterval(timer))
const ip = (addr: string) => addr.replace(/:\d+$/, '').replace(/^\[|\]$/g, '')
const summary = computed(() => {
    const l = list.value ?? []
    const now = l.filter((c) => !c.until).length
    const ips = new Set(l.map((c) => ip(c.addr))).size
    return `${l.length} session${l.length === 1 ? '' : 's'} from ${ips} address${ips === 1 ? '' : 'es'}${now ? ` · ${now} connected now` : ''}`
})
const hasSRT = computed(() => (list.value ?? []).some((c) => c.proto === 'srt'))
const hasLink = computed(() => hasSRT.value || (list.value ?? []).some((c) => c.tcp))
// resent of sent, %: SRT packets (mediamtx's count) or RTMP's TCP segments
const loss = (c: PullClient) =>
    c.proto === 'srt'
        ? (c.loss_pct ?? 0)
        : c.tcp?.segments
          ? ((c.tcp.retransmitted ?? 0) * 100) / c.tcp.segments
          : undefined
const shown = computed(() => (all.value ? list.value : list.value?.slice(0, 50)) ?? [])
const length = (c: PullClient) => dur(((c.until ? Date.parse(c.until) : node.now) - Date.parse(c.since)) / 1000)
</script>

<template>
    <div class="mt-3 rounded-md border border-line px-2.5 py-2 text-xs">
        <div class="mb-1 flex flex-wrap items-baseline gap-x-2">
            <b class="font-semibold">Clients</b>
            <span v-if="list" class="text-fg-2">{{ summary }}</span>
            <span v-else-if="failed" class="text-fg-3">not available from this node</span>
        </div>
        <div v-if="list && !list.length" class="text-fg-3">Nobody pulled the stream in this window.</div>
        <table v-else-if="list" class="w-full font-mono text-[11px] tabular-nums">
            <tr class="text-left text-fg-3">
                <th class="font-normal">address</th>
                <th class="font-normal">from</th>
                <th class="font-normal">for</th>
                <th class="text-right font-normal">sent</th>
                <template v-if="hasLink">
                    <th class="text-right font-normal" title="packets (SRT) or segments (RTMP) resent, of those sent">
                        loss
                    </th>
                    <th class="text-right font-normal" title="round trip (now; for a finished session, its last)">
                        rtt
                    </th>
                </template>
                <th
                    v-if="hasSRT"
                    class="text-right font-normal"
                    title="packets given up on: they could not be resent within the latency, the reader's picture broke"
                >
                    lost for good
                </th>
            </tr>
            <tr v-for="c in shown" :key="c.addr + c.since" class="border-t border-line">
                <td class="py-0.5 pr-2">
                    <span
                        v-if="!c.until"
                        class="mr-1 inline-block h-1.5 w-1.5 rounded-full bg-ok"
                        title="connected now"
                    />
                    <button
                        class="cursor-pointer border-0 bg-transparent p-0 font-mono text-[11px] text-fg hover:underline"
                        :title="`${c.addr}${c.user ? ' · user ' + c.user : ''}${c.agent ? ' · ' + c.agent : ''}: copy the address`"
                        @click="node.copy(ip(c.addr))"
                    >
                        {{ ip(c.addr) }}</button
                    ><span v-if="c.user" class="text-fg-3"> · {{ c.user }}</span>
                </td>
                <td class="pr-2 text-fg-2">{{ clock(Date.parse(c.since), true) }}</td>
                <td class="pr-2 text-fg-2">{{ length(c) }}{{ c.until ? '' : ', live' }}</td>
                <td class="text-right">{{ bytes(c.bytes) }}</td>
                <template v-if="hasLink">
                    <td class="text-right" :class="(loss(c) ?? 0) > 2 ? 'text-warn' : 'text-fg-2'">
                        {{ loss(c) === undefined ? '' : loss(c)!.toFixed(1) + ' %' }}
                    </td>
                    <td class="text-right text-fg-2">
                        {{ c.srt ? c.srt.rtt_ms.toFixed(0) + ' ms' : c.tcp ? c.tcp.rtt_ms.toFixed(0) + ' ms' : '' }}
                    </td>
                </template>
                <td v-if="hasSRT" class="text-right" :class="c.srt?.dropped ? 'text-bad' : 'text-fg-2'">
                    {{ c.srt ? c.srt.dropped.toLocaleString() : '' }}
                </td>
            </tr>
        </table>
        <button v-if="list && list.length > 50 && !all" class="mt-1 btn-ghost px-1.5 py-0 text-xs" @click="all = true">
            show all {{ list.length }}
        </button>
    </div>
</template>
